import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Responsive } from '@share/utils';
import { IAmenities, IHotelDetails } from '@common-types';
import { AmenityComponent } from '@components';

import './style.scss';
import { chunk } from 'lodash';

interface IProps {
  hotel: IHotelDetails;
  refAnchor: React.RefObject<HTMLDivElement>;
}

interface IState {
  isExpanded: boolean;
  displayExpandButton: boolean;
}

class AmenitiesComponent extends React.Component<IProps, IState> {
  state: IState = {
    isExpanded: false,
    displayExpandButton: false,
  }
  
  amenitiesContainerRef: React.RefObject<HTMLDivElement> = React.createRef();

  amenitiesList1Ref: React.RefObject<HTMLDivElement> = React.createRef();
  amenitiesList2Ref: React.RefObject<HTMLDivElement> = React.createRef();
  
  amenitiesTitleRef: React.RefObject<HTMLDivElement> = React.createRef();
  amenitiesDescriptionRef: React.RefObject<HTMLDivElement> = React.createRef();

  getAmenitiesByCategory = (nameGroup: string): IAmenities[] => {
    return (this.props.hotel.amenities || []).filter(
      (amenity) => amenity.categoryName === nameGroup,
    );
  };

  hasAmenities = (amenities: IAmenities[]) => {
    return (!!amenities && amenities.length)? 1 : 0;
  }

  getSplitedList = (amenitiesList: IAmenities[]) => {
    return chunk(amenitiesList, Math.floor(amenitiesList.length / 2) + 1);
  }

  render(): React.ReactNode {
    const { hotel, refAnchor } = this.props;
    const { amenityDescriptions } = hotel;
    const { isExpanded, displayExpandButton } = this.state;

    const general = this.getAmenitiesByCategory('General');
    const foodDrink = this.getAmenitiesByCategory('Food & Drink');
    const wellness = this.getAmenitiesByCategory('Wellness Facilities');
    const entertainment = this.getAmenitiesByCategory('Entertainment & Activities');
    const safety = this.getAmenitiesByCategory('Safety');
    const services = this.getAmenitiesByCategory('Services');
    const accessibility = this.getAmenitiesByCategory('Accessibility');
    const parking = this.getAmenitiesByCategory('Parking');
    const other = this.getAmenitiesByCategory('Other');

    const amenitiesTypeCount = this.hasAmenities(general) + this.hasAmenities(foodDrink) +
                          this.hasAmenities(wellness) + this.hasAmenities(entertainment) +
                          this.hasAmenities(safety) + this.hasAmenities(services) +
                          this.hasAmenities(accessibility) + this.hasAmenities(parking) +
                          this.hasAmenities(other);

    const fullAmenitiesList: IAmenities[] = [...general, ...foodDrink, ...wellness, ...entertainment, ...safety, ...services, ...accessibility, ...parking, ...other ].map(a => ({ ...a, categoryName: '' }));
    const splitedList = this.getSplitedList(fullAmenitiesList);

    const listHeight = (this.amenitiesList1Ref.current?.clientHeight || 0) + (this.amenitiesList2Ref.current?.clientHeight || 0);
    const titleHeight = (this.amenitiesTitleRef.current?.clientHeight || 0) + (this.amenitiesDescriptionRef.current?.clientHeight || 0);
    const containerHeight = (this.amenitiesContainerRef.current?.clientHeight || 0);
    const totalHeight = listHeight + titleHeight;
    const diffHeight = containerHeight - totalHeight;

    return (
      <div className={`amenities ${isExpanded ? 'expanded-amenities' : ''}`} ref={refAnchor}>
        <div className="amenities__container" ref={this.amenitiesContainerRef}>
          {!isExpanded && (diffHeight < 0 || displayExpandButton) && (
            <div className="amenities__expand-button" onClick={() => this.setState({ isExpanded: true, displayExpandButton: true })}>
              <FormattedMessage id="show.more" />
            </div>)}
          {isExpanded && (
            <div className="amenities__expand-button" onClick={() => this.setState({ isExpanded: false, displayExpandButton: true })}>
              <FormattedMessage id="show.less" />
            </div>)}

          <h4 ref={this.amenitiesTitleRef} className="amenities__title"><FormattedMessage id="tab.amenities" /></h4>

          {!!(amenityDescriptions && amenityDescriptions.length) &&
            amenityDescriptions.map((description, index) => (
              <p key={index} className="amenities__description" ref={this.amenitiesDescriptionRef}>
                {description}
              </p>))}

          {amenitiesTypeCount > 1 && (
            <div className="amenities__amenities-list" ref={this.amenitiesList1Ref}>
              <Responsive>
                <AmenityComponent amenities={general} />
              </Responsive>
              <Responsive>
                <AmenityComponent amenities={parking} />
              </Responsive>
              <div className="amenities__items amenities__default-markup">
                <Responsive>
                  <AmenityComponent amenities={foodDrink} />
                </Responsive>
                <Responsive>
                  <AmenityComponent amenities={wellness} />
                </Responsive>
                <Responsive>
                  <AmenityComponent amenities={entertainment} />
                </Responsive>
              </div>
              <div className="amenities__items">
                <Responsive>
                  <AmenityComponent amenities={safety} />
                </Responsive>
                <Responsive>
                  <AmenityComponent amenities={services} />
                </Responsive>
                <Responsive>
                  <AmenityComponent amenities={accessibility} />
                </Responsive>
                <Responsive>
                  <AmenityComponent amenities={other} />
                </Responsive>
                <div className="amenities__tablet-markup">
                  <Responsive>
                    <AmenityComponent amenities={foodDrink} />
                  </Responsive>
                  <Responsive>
                    <AmenityComponent amenities={wellness} />
                  </Responsive>
                  <Responsive>
                    <AmenityComponent amenities={entertainment} />
                  </Responsive>
                </div>
              </div>
            </div>)}

          {amenitiesTypeCount === 1 && (
            <div className="amenities__amenities-list desktop" ref={this.amenitiesList2Ref}>
              {splitedList.map((amenities, index) => (<div key={index} className="amenities__items">
                <Responsive>
                  <AmenityComponent amenities={amenities} />
                </Responsive>
              </div>))}
            </div>)}

          {amenitiesTypeCount === 1 && (
            <div className="amenities__amenities-list mobile">
              <Responsive>
                <AmenityComponent amenities={fullAmenitiesList} />
              </Responsive>
            </div>)}

        </div>
      </div>
    );
  }
}

export const Amenities = AmenitiesComponent;
